import * as React from 'react';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { dollarFormatter } from 'af-utils/format.util';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import { Button } from '@acceligentllc/storybook';

import BillingTypeIndicator from '../../BillingTypeIndicator';
import { toCSVData } from '../../helpers';
import BillableAndNonBillableWorkTable from '../Table/BillableAndNonBillableWorkTable';

interface Props {
	workSummary: WorkSummaryVM[];
	billingCodes: Record<string, W_BillingCode_FindForJob_VM_Single[]>;
	workSummaryDetails: WorkSummaryDetailVM[];
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailVM[]>;
	areFRsReadOnly: boolean;
	isWorkSummaryStatusCompleted: boolean;
	isWorkSummaryStatusReviewed: boolean;
	alternativeWorkSummary: Set<WorkSummaryVM>;
	workOrderCode: string;
	belongsToProject: boolean;
	subJobs: SiblingSubjobVM[];
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	onReallocateAll: (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => void;
	fieldReportId: number;
	workOrderId: number;
	onBulkEditDetailBillableStatus: (workSummaryDetailIds: number[], isBillable: boolean) => void;
}

const MaterialNonCost: React.FC<Props> = (props) => {
	const {
		workSummary,
		billingCodes,
		workSummaryDetails,
		onRowHighlight,
		highlightedGroup,
		isWorkSummaryStatusCompleted,
		areFRsReadOnly,
		isWorkSummaryStatusReviewed,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		alternativeWorkSummary,
		belongsToProject,
		handleJobReallocation,
		subJobs,
		onReallocateAll,
		fieldReportId,
		workOrderId,
		workOrderCode,
		onBulkEditDetailBillableStatus,
	} = props;

	const totalMaterialNonCost = workSummary.reduce((_acc, _curr) => {
		return _acc + (_curr.materialCost ?? 0);
	}, 0);
	const showHint = totalMaterialNonCost === 0;

	const headerClassName = bemElement('field-report__work-summary__billable-work', 'header');
	const nonBillableWorkClassName = bemBlock('field-report__work-summary__billable-work', { 'non-billable': true });

	const handleBulkEditDetailBillableStatus = React.useCallback((workSummaryDetailIds: number[]) => {
		onBulkEditDetailBillableStatus(workSummaryDetailIds, true);
	}, [onBulkEditDetailBillableStatus]);

	const onExportClick = React.useCallback(() => {
		const csvData = toCSVData(workSummary, belongsToProject, WorkSummaryBillingType.MATERIAL_COST);
		downloadCSV(csvData, `${workOrderCode}_material_non_cost.csv`);
	}, [belongsToProject, workOrderCode, workSummary]);

	const isWorkSummaryReadonly = isWorkSummaryStatusCompleted || areFRsReadOnly || isWorkSummaryStatusReviewed;
	const isBillableStatusEditable = !(isWorkSummaryStatusCompleted || areFRsReadOnly);

	return (
		<div className={nonBillableWorkClassName}>
			<div className={headerClassName}>
				<BillingTypeIndicator billingType={WorkSummaryBillingType.MATERIAL_COST} />
				<span className={bemElement(headerClassName, 'title')}>
					Material Non-Expenses
				</span>
				<Button
					disabled={!workSummary.length}
					icon="ActionDownload"
					label="Export"
					onClick={onExportClick}
					style="link"
				/>
			</div>
			<BillableAndNonBillableWorkTable
				alternativeWorkSummary={alternativeWorkSummary}
				belongsToProject={belongsToProject}
				billingCodes={billingCodes}
				billingType={WorkSummaryBillingType.MATERIAL_COST}
				fieldReportId={fieldReportId}
				handleJobReallocation={handleJobReallocation}
				highlightedGroup={highlightedGroup}
				highlightedGroupKey={highlightedGroupKey}
				isBillable={false}
				isBillableStatusEditable={isBillableStatusEditable}
				isReadOnly={isWorkSummaryReadonly}
				onBulkEditDetailBillableStatus={handleBulkEditDetailBillableStatus}
				onReallocateAll={onReallocateAll}
				onRowHighlight={onRowHighlight}
				subJobs={subJobs}
				workOrderId={workOrderId}
				workSummary={workSummary}
				workSummaryDetails={workSummaryDetails}
				workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
			/>
			<div className={bemElement('field-report__work-summary__billable-work', 'total')}>
				{showHint && (
					<span className={bemElement('field-report__work-summary__billable-work__total', 'hint')}>
						Non-expense total is calculated after work with a material cost is moved to the non-expenses table
					</span>
				)}
				{`Non-expense total ${dollarFormatter.format(totalMaterialNonCost)}`}
			</div>
		</div>
	);
};

export default React.memo(MaterialNonCost);
